// Feed View - Global feeds (read-only)

import { getTruths, type SortOption } from '../api/truths';
import type { FeedItem } from '../api/types';
import { createFeedItemCard } from '../components/TruthCard';
import { createLoadingIndicator } from '../router';
import { icon } from '../icons';

let currentOffset = 0;
let isLoading = false;
let hasMore = true;
const PAGE_SIZE = 25;

// Auto-refresh state
const INACTIVITY_TIMEOUT = 60000; // 1 minute
let autoRefreshTimer: number | null = null;
let isPaused = false;
let lastActivityTime = Date.now();
let activityCheckTimer: number | null = null;
let currentView: HTMLElement | null = null;
let currentTab = 'recent';

async function loadTruths(sort: SortOption, offset: number): Promise<FeedItem[]> {
  const response = await getTruths(sort, PAGE_SIZE, offset);
  return (response.data || []) as FeedItem[];
}

// Activity tracking
function recordActivity(): void {
  lastActivityTime = Date.now();
  // If paused due to inactivity and user interacts, resume
  if (!currentView) return;
  const controlBar = currentView.querySelector('.auto-refresh-control');
  if (controlBar?.classList.contains('auto-refresh-control--inactive')) {
    resumeAutoRefresh();
  }
}

function checkInactivity(): void {
  const now = Date.now();
  if (now - lastActivityTime >= INACTIVITY_TIMEOUT && !isPaused) {
    pauseAutoRefresh(true); // true = paused due to inactivity
  }
}

function setupActivityTracking(): void {
  const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach(event => {
    document.addEventListener(event, recordActivity, { passive: true });
  });
  // Check for inactivity every 10 seconds
  activityCheckTimer = window.setInterval(checkInactivity, 10000);
}

function cleanupActivityTracking(): void {
  const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach(event => {
    document.removeEventListener(event, recordActivity);
  });
  if (activityCheckTimer) {
    clearInterval(activityCheckTimer);
    activityCheckTimer = null;
  }
}

// Auto-refresh control
function createAutoRefreshControl(): HTMLElement {
  const control = document.createElement('div');
  control.className = 'auto-refresh-control';

  // Progress bar background
  const progressBar = document.createElement('div');
  progressBar.className = 'auto-refresh-progress';
  control.appendChild(progressBar);

  const statusDot = document.createElement('span');
  statusDot.className = 'auto-refresh-dot';

  const statusText = document.createElement('span');
  statusText.className = 'auto-refresh-status';
  statusText.textContent = 'LIVE';

  const countdown = document.createElement('span');
  countdown.className = 'auto-refresh-countdown';
  countdown.textContent = '5s';

  const pauseBtn = document.createElement('button');
  pauseBtn.className = 'auto-refresh-btn';
  pauseBtn.innerHTML = `${icon('pause', 14)} PAUSE`;
  pauseBtn.addEventListener('click', () => {
    if (isPaused) {
      resumeAutoRefresh();
    } else {
      pauseAutoRefresh(false);
    }
  });

  control.appendChild(statusDot);
  control.appendChild(statusText);
  control.appendChild(countdown);
  control.appendChild(pauseBtn);

  return control;
}

function updateControlUI(dueToInactivity = false): void {
  if (!currentView) return;
  const control = currentView.querySelector('.auto-refresh-control');
  if (!control) return;

  const statusText = control.querySelector('.auto-refresh-status') as HTMLElement;
  const pauseBtn = control.querySelector('.auto-refresh-btn') as HTMLButtonElement;
  const countdownEl = control.querySelector('.auto-refresh-countdown') as HTMLElement;
  const progressBar = control.querySelector('.auto-refresh-progress') as HTMLElement;

  if (isPaused) {
    control.classList.add('auto-refresh-control--paused');
    if (dueToInactivity) {
      control.classList.add('auto-refresh-control--inactive');
      statusText.textContent = 'PAUSED (inactive)';
    } else {
      control.classList.remove('auto-refresh-control--inactive');
      statusText.textContent = 'PAUSED';
    }
    pauseBtn.innerHTML = `${icon('play', 14)} RESUME`;
    if (countdownEl) countdownEl.textContent = '--';
    if (progressBar) {
      progressBar.classList.remove('auto-refresh-progress--filling');
      progressBar.style.width = '0%';
    }
  } else {
    control.classList.remove('auto-refresh-control--paused', 'auto-refresh-control--inactive');
    statusText.textContent = 'LIVE';
    pauseBtn.innerHTML = `${icon('pause', 14)} PAUSE`;
    if (countdownEl) countdownEl.textContent = '5s';
  }
}

function pauseAutoRefresh(dueToInactivity = false): void {
  isPaused = true;
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
  updateControlUI(dueToInactivity);
}

function resumeAutoRefresh(): void {
  isPaused = false;
  lastActivityTime = Date.now();
  startAutoRefresh();
  updateControlUI();
}

function startAutoRefresh(): void {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
  }

  let countdown = 5;

  // Start the progress bar animation immediately
  if (currentView) {
    const progressBar = currentView.querySelector('.auto-refresh-progress') as HTMLElement;
    if (progressBar) {
      progressBar.classList.remove('auto-refresh-progress--filling');
      progressBar.offsetHeight; // Force reflow
      progressBar.classList.add('auto-refresh-progress--filling');
    }
  }

  autoRefreshTimer = window.setInterval(async () => {
    // Check if view is still in DOM, cleanup if not
    if (!currentView || !document.body.contains(currentView)) {
      stopAutoRefresh();
      return;
    }

    if (isPaused) return;

    // Query elements inside the interval so they're found after view is in DOM
    const countdownEl = currentView.querySelector('.auto-refresh-countdown') as HTMLElement;
    const progressBar = currentView.querySelector('.auto-refresh-progress') as HTMLElement;

    countdown--;

    if (countdown <= 0) {
      // Show 0s briefly at 100% before refreshing
      if (countdownEl) {
        countdownEl.textContent = '0s';
      }

      // Trigger pulse animation
      const feedContainer = currentView.querySelector('.feed-container') as HTMLElement;
      if (feedContainer) {
        feedContainer.classList.add('feed-container--shaking');
        await new Promise(resolve => setTimeout(resolve, 400));
        feedContainer.classList.remove('feed-container--shaking');
      }

      // Refresh the feed
      await refreshFeedSilent(currentView, currentTab);

      // Reset for next cycle
      countdown = 5;
      if (countdownEl) {
        countdownEl.textContent = '5s';
      }
      // Restart progress bar animation
      if (progressBar) {
        progressBar.classList.remove('auto-refresh-progress--filling');
        progressBar.offsetHeight; // Force reflow
        progressBar.classList.add('auto-refresh-progress--filling');
      }
    } else {
      if (countdownEl) {
        countdownEl.textContent = `${countdown}s`;
      }
    }
  }, 1000);
}

function stopAutoRefresh(): void {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
  cleanupActivityTracking();
}

// Silent refresh that preserves scroll position and adds new items smoothly
async function refreshFeedSilent(view: HTMLElement, tab: string): Promise<void> {
  if (isLoading) return;

  try {
    const truths = await loadTruths(tab as SortOption, 0);
    const feedContainer = view.querySelector('.feed-container') as HTMLElement;

    if (truths.length > 0 && feedContainer) {
      // Get existing truth IDs
      const existingIds = new Set(
        Array.from(feedContainer.querySelectorAll('.truth-card'))
          .map(card => card.getAttribute('data-truth-id'))
      );

      // Find new truths
      const newTruths = truths.filter(t => !existingIds.has(t.id.toString()));

      if (newTruths.length > 0) {
        // For sorted feeds (top, trending), replace content to maintain sort order
        // For chronological feed (recent), prepend new items at top
        if (tab === 'top' || tab === 'trending') {
          // Replace existing cards with fresh sorted data
          const existingCards = feedContainer.querySelectorAll('.truth-card');

          // Build the new feed content
          const fragment = document.createDocumentFragment();
          truths.forEach(truth => {
            const card = createFeedItemCard(truth);
            // Highlight cards that are new
            if (!existingIds.has(truth.id.toString())) {
              card.classList.add('truth-card--new');
            }
            fragment.appendChild(card);
          });

          // Remove old cards and add new sorted ones
          existingCards.forEach(card => card.remove());
          feedContainer.appendChild(fragment);
        } else {
          // Chronological: add new truths at the top with animation
          // API returns newest-first, so insert in same order
          const fragment = document.createDocumentFragment();
          newTruths.forEach(truth => {
            const card = createFeedItemCard(truth);
            card.classList.add('truth-card--new');
            fragment.appendChild(card);
          });

          feedContainer.insertBefore(fragment, feedContainer.firstChild);
        }

        // Flash the control bar to indicate new content
        const control = view.querySelector('.auto-refresh-control');
        control?.classList.add('auto-refresh-control--flash');
        setTimeout(() => control?.classList.remove('auto-refresh-control--flash'), 600);
      }
    }
  } catch (err) {
    console.error('Silent refresh failed:', err);
  }
}

function createFeedTabs(activeTab: string, onTabChange: (tab: string) => void): HTMLElement {
  const tabs = document.createElement('div');
  tabs.className = 'feed-tabs';

  const tabsConfig = [
    { id: 'recent', icon: icon('clock', 14), label: 'Recent' },
    { id: 'trending', icon: icon('trending', 14), label: 'Trending' },
    { id: 'top', icon: icon('top', 14), label: 'Top' },
  ];

  tabsConfig.forEach(({ id, icon: iconHtml, label }) => {
    const btn = document.createElement('button');
    btn.className = `feed-tab ${activeTab === id ? 'feed-tab--active' : ''}`;
    btn.innerHTML = `${iconHtml} ${label}`;
    btn.addEventListener('click', () => onTabChange(id));
    tabs.appendChild(btn);
  });

  return tabs;
}

export async function createFeedView(params: Record<string, string>): Promise<HTMLElement> {
  // Cleanup any existing auto-refresh
  stopAutoRefresh();

  const view = document.createElement('div');
  view.className = 'feed-view';
  currentView = view;

  // Determine initial tab from route
  let activeTab = params.tab || 'recent';
  if (window.location.hash === '#/trending') {
    activeTab = 'trending';
  }
  currentTab = activeTab;

  // Reset pagination and auto-refresh state
  currentOffset = 0;
  hasMore = true;
  isPaused = false;
  lastActivityTime = Date.now();

  const availableTabs = ['recent', 'trending', 'top'];

  // Auto-refresh control bar
  const autoRefreshControl = createAutoRefreshControl();
  view.appendChild(autoRefreshControl);

  // Tabs
  const tabs = createFeedTabs(activeTab, async (tab) => {
    activeTab = tab;
    currentTab = tab;
    currentOffset = 0;
    hasMore = true;

    // Update active tab
    view.querySelectorAll('.feed-tab').forEach((btn, index) => {
      btn.classList.toggle('feed-tab--active', availableTabs[index] === tab);
    });

    await refreshFeed(view, tab);
  });
  view.appendChild(tabs);

  // Feed container
  const feedContainer = document.createElement('div');
  feedContainer.className = 'feed-container';
  view.appendChild(feedContainer);

  // Load more button
  const loadMoreContainer = document.createElement('div');
  loadMoreContainer.className = 'load-more';
  loadMoreContainer.innerHTML = '<button class="btn btn--secondary load-more-btn">Load More</button>';
  view.appendChild(loadMoreContainer);

  const loadMoreBtn = loadMoreContainer.querySelector('.load-more-btn') as HTMLButtonElement;
  loadMoreBtn.addEventListener('click', () => loadMore(feedContainer, activeTab, loadMoreBtn));

  // Initial load
  await refreshFeed(view, activeTab);

  // Start auto-refresh and activity tracking
  setupActivityTracking();
  startAutoRefresh();

  // Infinite scroll
  const observer = new IntersectionObserver(
    async (entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoading) {
        await loadMore(feedContainer, activeTab, loadMoreBtn);
      }
    },
    { threshold: 0.1 }
  );
  observer.observe(loadMoreContainer);

  return view;
}

async function refreshFeed(view: HTMLElement, tab: string): Promise<void> {
  const feedContainer = view.querySelector('.feed-container') as HTMLElement;
  const hadExistingContent = feedContainer.children.length > 0;

  // Only show loading indicator if feed is empty
  let loading: HTMLElement | null = null;
  if (!hadExistingContent) {
    loading = createLoadingIndicator();
    feedContainer.appendChild(loading);
  }

  try {
    const truths = await loadTruths(tab as SortOption, 0);

    loading?.remove();

    // Only clear feed after successful load
    feedContainer.innerHTML = '';
    currentOffset = 0;

    if (truths.length === 0) {
      feedContainer.innerHTML = `
        <div class="empty">
          <div class="empty__icon">${icon('crab', 48)}</div>
          <p class="empty__text">No truths yet</p>
        </div>
      `;
      hasMore = false;
    } else {
      truths.forEach((truth) => {
        feedContainer.appendChild(createFeedItemCard(truth));
      });
      hasMore = truths.length === PAGE_SIZE;
      currentOffset = truths.length;
    }
  } catch (err) {
    loading?.remove();
    // Preserve existing content on failure - only show error if feed was empty
    if (!hadExistingContent) {
      feedContainer.innerHTML = `
        <div class="empty">
          <div class="empty__icon">${icon('error', 48)}</div>
          <p class="empty__text">Failed to load feed</p>
        </div>
      `;
    }
    console.error('Feed refresh failed:', err);
  }

  // Update load more button visibility
  const loadMoreBtn = view.querySelector('.load-more-btn') as HTMLButtonElement;
  if (loadMoreBtn) {
    loadMoreBtn.style.display = hasMore ? 'inline-block' : 'none';
  }
}

async function loadMore(feedContainer: HTMLElement, tab: string, loadMoreBtn: HTMLButtonElement): Promise<void> {
  if (isLoading || !hasMore) return;

  isLoading = true;
  loadMoreBtn.disabled = true;
  loadMoreBtn.textContent = 'Loading...';

  try {
    const truths = await loadTruths(tab as SortOption, currentOffset);

    truths.forEach((truth) => {
      feedContainer.appendChild(createFeedItemCard(truth));
    });

    currentOffset += truths.length;
    hasMore = truths.length === PAGE_SIZE;
  } catch (err) {
    console.error('Failed to load more:', err);
  } finally {
    isLoading = false;
    loadMoreBtn.disabled = false;
    loadMoreBtn.textContent = 'Load More';
    loadMoreBtn.style.display = hasMore ? 'inline-block' : 'none';
  }
}

export async function createTrendingView(): Promise<HTMLElement> {
  return createFeedView({ tab: 'trending' });
}
